import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: number;
  username: string;
  useremail: string;
  balance: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (userName: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
  updateBalance: (amount: number) => void;
  isLoading: boolean;
  error: string | null;
  gameUrl: string | null;
  saveGameUrl: (url: string) => void;
  updateUserData: () => void;
  notifications: number;
  isChatLoading: boolean;
  chatInfos: string;
  fetchChatInfos: () => void;
  allGames: any[];
  setAllGames: (games: any[]) => void;
  isGamesLoaded: boolean;
  setIsGamesLoaded: (loaded: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [gameUrl, setGameUrl] = useState<string | null>(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatInfos, setChatInfos] = useState('');
  const [allGames, setAllGames] = useState([]);
  const [isGamesLoaded, setIsGamesLoaded] = useState(false);

  useEffect(() => {
    updateUserData();
  }, []);
  // useEffect(() => {
  //   // Check for stored auth token and user data on mount
  //   const storedUser = localStorage.getItem('userData');

  //   if (storedUser) {
  //     try {
  //       setUser(JSON.parse(storedUser));
  //       setIsAuthenticated(true);
  //     } catch (err) {
  //       console.error('Failed to parse stored user data:', err);
  //       localStorage.removeItem('userData');
  //     }
  //   }
  // }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('https://user-api.u-slot.vip/api/Auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: allows cookies to be sent and received
        body: JSON.stringify({
          Username: email,
          Password: password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Assuming the API returns a token and user data
      // Adjust these field names based on your actual API response
      const { id, username, balance } = data.data;

      // Map API user data to your User interface
      const userData: User = {
        id: id,
        username: username,
        useremail: '',
        balance: balance,
      };

      // Store token and user data
      // localStorage.setItem('userData', JSON.stringify(userData));

      setUser(userData);
      setIsAuthenticated(true);
      setIsLoading(false);

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during login';
      setError(errorMessage);
      setIsLoading(false);
      console.error('Login error:', err);
      return false;
    }
  };

  const signup = async (userName: string, email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('https://user-api.u-slot.vip/api/Auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: allows cookies to be sent and received
        body: JSON.stringify({
          username: userName,
          email: email,
          passwordHash: password,
          passwordConfrim: password,
          parentCode: 'Admin'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Singup failed');
      }

      setIsLoading(false);

      if (data.code == 0) {
        return true;
      } else {
        console.log(data.message)
        setError(data.message);
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during signup';
      setError(errorMessage);
      setIsLoading(false);
      console.error('Signup error:', err);
      return false;
    }
  };

  const updateUserData = async () => {
    try {
      const response = await fetch('https://user-api.u-slot.vip/api/Game/get_real_data', {
        method: 'POST',
        credentials: 'include', // Include cookies if needed for authentication
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.code == 0) {
          // Adjust based on your API response structure
          // console.log(user);
          setUser({
            id: (user != null) ? user.id : 0,
            username: data.data.username,
            useremail: '',
            balance: data.data.balance
          });
          setIsAuthenticated(true);
          setNotifications(data.data.noticeIds.length);

          // Call parent callback if provided
          // if (onBalanceUpdate) {
          // onBalanceUpdate(newBalance);
          // }
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setError(null);
        // localStorage.removeItem('userData');
      }
    } catch (error) {
      console.error('Failed fetch data:', error);
    }
  }

  const logout = async () => {
    try {
      // Call the logout API
      await fetch('https://user-api.u-slot.vip/api/Auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: allows cookies to be sent and received
      });
    } catch (err) {
      console.error('Logout API error:', err);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local state and storage regardless of API response
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      // localStorage.removeItem('userData');
    }
  };

  const fetchChatInfos = async () => {
    setIsChatLoading(true);
    try {
      const response = await fetch("https://user-api.u-slot.vip/api/Alarm/get_comment_list", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: allows cookies to be sent and received
        body: JSON.stringify({
          page: 1,
          pageSize: 9999
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setChatInfos(JSON.stringify(data.data.items));
      } else {
        console.log(response)
      }
    } catch (err) {
      console.log("ChatInfo Fetching Err", err)
    } finally {
      setIsChatLoading(false);
    }
  };

  const updateBalance = (amount: number) => {
    if (user) {
      const updatedUser = { ...user, balance: user.balance + amount };
      setUser(updatedUser);
      // localStorage.setItem('userData', JSON.stringify(updatedUser));
    }
  };

  const saveGameUrl = (url: string) => {
    setGameUrl(url)
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        login,
        signup,
        logout,
        updateBalance,
        isLoading,
        error,
        gameUrl,
        saveGameUrl,
        updateUserData,
        notifications,
        isChatLoading,
        chatInfos,
        fetchChatInfos,
        allGames,
        setAllGames,
        isGamesLoaded,
        setIsGamesLoaded
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}